import {
  Button,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  type DropdownMenuItem,
  type EmptyStateSettings,
  ImportIcon,
  Table,
  useDefaultSorting,
} from '@bp/ui-components';
import { useCopyToClipboard } from '@uidotdev/usehooks';
import { type DocumentType, graphql } from 'client/gql';
import { useConfirm } from 'hooks/useConfirm';
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { handleFileEntryDownload } from '../../utils/download';
import { formatBytes } from '../../utils/formatBytes';
import { getFileUrl } from '../../utils/getFileUrl';
import { mimeTypes } from '../../utils/mime-types-map';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { BpLink } from '../BpLink/BpLink';

const DeleteFileEntryMutationDocument = graphql(`
  mutation BpDeleteFileEntry($where: FileEntryWhere!) {
    deleteFileEntries(where: $where) {
      nodesDeleted
      relationshipsDeleted
    }
  }
`);

export const FileTableFieldsDocument = graphql(`
  fragment FileTableFields on FileEntry {
    uuid
    __typename
    mediaType
    size
    publicDownloadToken
    downloadToken
    filename
  }
`);

type FileTableProps = {
  mode: 'show' | 'edit';
  files: FileEntryTableType[] | undefined | null;
  isGroupEditor: boolean;
  fullHeight?: boolean;
  className?: string | undefined;
  onDeleted?: (uuid: string) => void;
  onRenamed?: (uuid: string, newName: string) => void;
  isDesktop?: boolean;
  padding?: string;
  emptyStateSettings?: EmptyStateSettings;
};

export type FileEntryTableType = DocumentType<typeof FileTableFieldsDocument>;

export const FileTable: FC<FileTableProps> = ({
  mode,
  className,
  files = [],
  onDeleted,
  onRenamed,
  isDesktop,
  padding = 'var(--spacing-6)',
  emptyStateSettings,
  isGroupEditor,
  fullHeight,
}) => {
  const { t } = useTranslation();
  const [, copyToClipboard] = useCopyToClipboard();

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('files.titleSingular'),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const [, deleteFile] = useMutation(DeleteFileEntryMutationDocument);
  const onFileDelete = async (uuid: string) => {
    await deleteFile({ where: { uuid: uuid } });
    onDeleted && onDeleted(uuid);
  };

  const filesWithMimeType: FileEntryTableType[] =
    files?.map((file) => {
      return {
        ...file,
        mediaType: mimeTypes[file.mediaType] ? mimeTypes[file.mediaType] : file.mediaType,
        formatSize: formatBytes(file.size),
      };
    }) ?? [];

  /* RenameModal */
  const [_isOpen, setIsOpen] = React.useState(false);
  const [_file, setFile] = React.useState<FileEntryTableType | null>(null);

  const _handleModalClose = () => {
    setFile(null);
    setIsOpen(false);
  };

  function openModal(file: FileEntryTableType) {
    setFile(file);
    setIsOpen(true);
  }

  const { sorting, onSortingChange } = useDefaultSorting([{ id: 'filename', desc: false }]);

  const fileDropdownEditor = (file: FileEntryTableType): DropdownMenuItem[] => [
    {
      onClick: async () => {
        if (file.downloadToken) {
          await handleFileEntryDownload(file);
        }
      },
      label: t('common.download'),
    },
    {
      onClick: () => {
        openModal(file);
      },
      label: t('common.rename'),
    },
    {
      onClick: async () => {
        if (file.publicDownloadToken) {
          await copyToClipboard(getFileUrl(file.uuid, file.publicDownloadToken));
          showSuccessToast(t('common.copySuccess'));
        }
      },
      label: t('common.copyType', { type: 'Link' }),
      disabled: !file.publicDownloadToken,
    },
    { type: 'ruler' },
    {
      label: t('delete.delete'),
      color: 'error',
      onClick: async () => {
        const res = await confirmDelete();
        if (res) {
          await onFileDelete(file.uuid);
        }
      },
    },
  ];

  return (
    <>
      <div className={className}>
        <Table<FileEntryTableType>
          maxHeight={!fullHeight ? 210 : undefined}
          canScroll={!fullHeight}
          customPadding={padding}
          isDesktop={isDesktop}
          data={filesWithMimeType}
          columns={[
            {
              header: t('common.filename'),
              accessorKey: 'filename',
              id: 'filename',
              size: 400,
              canExpand: true,
              cell: ({ row }) => {
                return (
                  <BpLink
                    value={row.original.filename}
                    onNavigate={async () => {
                      await handleFileEntryDownload(row.original);
                    }}
                    isEditorOfCourse={isGroupEditor}
                  />
                );
              },
            },
            {
              header: t('common.mediaType'),
              accessorKey: 'mediaType',
              id: 'mediaType',
              size: 60,
            },
            {
              header: t('common.size'),
              accessorKey: 'formatSize',
              id: 'formatSize',
              size: 100,
            },
          ]}
          hideHeader={filesWithMimeType.length === 0}
          emptyStateSettings={
            emptyStateSettings ?? {
              title: '',
              size: 'small',
              subtitle: t('files.noFiles'),
              padding: 'l',
            }
          }
          showSort
          sorting={sorting}
          onSortingChange={onSortingChange}
          lastColWidth='var(--spacing-6)'
          lastCol={(row) => {
            const currentFile = files?.find((fe) => fe.uuid === row.original.uuid);

            return (
              <div>
                {mode === 'show' && !!currentFile && (
                  <Button
                    hierarchy='ghost'
                    icon={
                      <ImportIcon
                        onClick={async () => {
                          if (currentFile && currentFile.downloadToken) {
                            await handleFileEntryDownload(currentFile);
                          }
                        }}
                      />
                    }
                  />
                )}
                {mode === 'edit' && !!onFileDelete && (
                  <Dropdown
                    usePortal={false}
                    noPadding
                    trigger={<Button hierarchy='ghost' icon={<DotsHorizontalIcon />} />}
                  >
                    <DropdownMenu data={fileDropdownEditor(row.original)} />
                  </Dropdown>
                )}
              </div>
            );
          }}
        />
        {/* {isOpen && file && (
          <Modal isOpen={isOpen} onRequestClose={handleModalClose} title={t('common.rename')} width='s'>
            <FileRenameForm
              file={file}
              onClose={handleModalClose}
              onRenamed={(uuid, newName) => {
                onRenamed && onRenamed(uuid, newName);
              }}
            />
          </Modal>
        )} */}
      </div>

      <ConfirmationDialog />
    </>
  );
};
